import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { navigationSections, NavItem, NavSection } from '@/config/navigation';

function isActive(pathname: string, href?: string) {
  if (!href) return false;
  if (href === '/') return pathname === '/';
  if (pathname === href) return true;
  return pathname.startsWith(href + '/');
}

function NavIcon({ name, className = 'w-4 h-4' }: { name?: string; className?: string }) {
  switch (name) {
    case 'dashboard':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      );
    case 'alert':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      );
    case 'chart':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      );
    case 'marketplace':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      );
    case 'orders':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      );
    case 'catalog':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      );
    case 'purchasing':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      );
    case 'inventory':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      );
    case 'finance':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'admin':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    default:
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 12h16M4 18h7" />
        </svg>
      );
  }
}

export default function Sidebar() {
  const { pathname } = useRouter();
  const { hasPermission } = useAuth();
  
  // By default, dropdowns start closed, except the active group for the current route
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  // Ensure the menu group containing the active page is open and remains open when navigating
  useEffect(() => {
    navigationSections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.children) {
          const hasActiveChild = item.children.some((c) => isActive(pathname, c.href));
          if (hasActiveChild) {
            setOpenGroups((prev) => ({
              ...prev,
              [item.label]: true,
            }));
          }
        }
      });
    });
  }, [pathname]);

  function toggleGroup(label: string) {
    setOpenGroups((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  }

  return (
    <aside className="fixed bottom-0 start-0 top-16 hidden w-72 overflow-y-auto border-r border-gray-200 bg-white px-3 py-4 lg:block select-none shadow-[1px_0_3px_rgba(0,0,0,0.02)]">
      <nav className="flex flex-col space-y-6">
        {navigationSections.map((section, sIdx) => {
          const visibleItems = section.items.filter(
            (item) => !item.permission || hasPermission(item.permission),
          );

          if (visibleItems.length === 0) return null;

          return (
            <div key={section.title || sIdx} className="space-y-1">
              {section.title && (
                <div className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 font-heading">
                  {section.title}
                </div>
              )}

              {visibleItems.map((item) => {
                if (item.children) {
                  const visibleChildren = item.children.filter(
                    (child) => !child.permission || hasPermission(child.permission),
                  );
                  if (visibleChildren.length === 0) return null;

                  const isOpen = !!openGroups[item.label];
                  const groupActive = visibleChildren.some((child) => isActive(pathname, child.href));

                  return (
                    <div key={item.label} className="space-y-0.5">
                      <button
                        type="button"
                        onClick={() => toggleGroup(item.label)}
                        className={`group flex w-full items-center justify-between rounded-lg px-3 py-2 text-start text-xs font-semibold transition-all cursor-pointer ${
                          groupActive
                            ? 'bg-accent/10 text-accent font-bold'
                            : 'text-gray-700 hover:bg-gray-100/70 hover:text-gray-900'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <div
                            className={`p-1 rounded-md transition-all duration-200 ${
                              groupActive
                                ? 'bg-accent/20 text-accent'
                                : 'text-gray-500 group-hover:text-accent group-hover:bg-accent/10'
                            }`}
                          >
                            <NavIcon
                              name={item.icon}
                              className="w-4 h-4"
                            />
                          </div>
                          <span>{item.label}</span>
                        </div>
                        <svg
                          className={`w-3.5 h-3.5 shrink-0 transition-transform duration-300 ease-in-out ${
                            isOpen ? 'rotate-180 text-accent' : 'text-gray-400 group-hover:text-gray-600'
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {/* Smooth Sliding Dropdown Submenu */}
                      <div
                        className="overflow-hidden transition-all duration-300 ease-in-out"
                        style={{
                          maxHeight: isOpen ? '400px' : '0px',
                          opacity: isOpen ? 1 : 0,
                          transform: isOpen ? 'translateY(0)' : 'translateY(-4px)',
                          transition:
                            'max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease, transform 0.25s ease',
                        }}
                      >
                        <div className="ms-4 flex flex-col space-y-0.5 border-s-2 border-accent/25 ps-2.5 pt-1 pb-1">
                          {visibleChildren.map((child) => {
                            const childActive = isActive(pathname, child.href);
                            return (
                              <Link
                                key={child.href}
                                href={child.disabled ? '#' : child.href}
                                aria-disabled={child.disabled}
                                className={`group flex items-center justify-between rounded-md px-2.5 py-1.5 text-xs transition-colors ${
                                  child.disabled
                                    ? 'cursor-not-allowed text-gray-400'
                                    : childActive
                                      ? 'bg-accent font-semibold text-white shadow-sm'
                                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                }`}
                              >
                                <span className="truncate">{child.label}</span>
                                {child.badge && (
                                  <span
                                    className={`text-[10px] px-1.5 py-0.2 rounded font-mono ${
                                      childActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    {child.badge}
                                  </span>
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }

                const active = isActive(pathname, item.href);
                return (
                  <Link
                    key={item.label}
                    href={item.href ?? '#'}
                    className={`flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all ${
                      active
                        ? 'bg-accent text-white shadow-sm font-bold'
                        : 'text-gray-700 hover:bg-gray-100/70 hover:text-gray-900'
                    }`}
                  >
                    <NavIcon
                      name={item.icon}
                      className={`w-4 h-4 ${active ? 'text-white' : 'text-gray-500'}`}
                    />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
